import { useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';

import AdminFormPage from 'components/admin/shared/AdminFormPage';
import Button from 'components/shared/Button';
import ButtonGroup from 'components/shared/ButtonGroup';
import CircularProgress from 'components/shared/CircularProgress';
import FormHeader from 'components/shared/form/FormHeader';
import { Select } from 'components/shared/form/InfiniteScrollDropdown';
import InputGroup from 'components/shared/form/InputGroup';
import { useProfile } from 'context/profile';
import { getFieldError, useAdminForm } from 'hooks/datasource';
import useInfiniteOptions from 'hooks/useInfiniteOptions';
import type { Group } from 'models/flexible/Group';
import { PERMISSIONS } from 'models/flexible/Profile';
import {
  aiCriteriaSetsQueryKeys,
  createAICriteriaSet,
  showAICriteriaSet,
  updateAICriteriaSet,
} from 'services/api/admin/aiCriteriaSets';
import { adminIndexClients } from 'services/api/admin/clients';
import { adminIndexGroups } from 'services/api/admin/groups';
import type { AdminGroupIndexFields } from 'services/api/fields/admin/group';

import CategoriesList, { type CriteriaItem, generateTempCategoryId } from './CategoriesList';
import { editAICriteriaSetValidation } from './editAICriteriaSetValidation';
import type { CriteriaCategory } from './NewCategoryModal';

const getId = (o: any) => o?.id as string;
const getName = (o: any) => o?.name as string;

type ScopeableGroup = Group<AdminGroupIndexFields<'groupType'>>;

type FormValues = {
  client: { id: string; name: string } | null;
  group: ScopeableGroup | null;
  categories: CriteriaCategory[];
  aiCriteria: CriteriaItem[];
};

export default function EditAICriteriaSet() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const isCopyMode = searchParams.get('copy') === 'true';
  const isEditing = !!id && !isCopyMode;
  const { profile } = useProfile();
  const canEdit = profile.has(PERMISSIONS.adminAICriteriaEdit);
  const readOnly = isEditing && !canEdit;

  const form = useAdminForm({
    id,
    show: showAICriteriaSet,
    include: ['group', 'categories', 'criteria', 'client'] as const,
    queryKey: aiCriteriaSetsQueryKeys,
    schema: editAICriteriaSetValidation,
    create: (payload: any) => createAICriteriaSet(payload),
    update: isCopyMode
      ? (_id: string, payload: any) => createAICriteriaSet(payload)
      : (_id: string, payload: any) => updateAICriteriaSet({ id: _id, ...payload }),
    toFormValues: (data: any): FormValues => {
      if (!data) {
        return {
          client: null,
          group: null,
          categories: [],
          aiCriteria: [],
        };
      }

      return {
        client: isCopyMode ? null : (data.client ?? null),
        group: isCopyMode ? null : (data.group ?? null),
        categories: (data.categories ?? [])
          .map((c: any) => ({
            _tempId: generateTempCategoryId(),
            name: c.name,
            color: c.color,
            order: c.order,
          }))
          .sort((a: CriteriaCategory, b: CriteriaCategory) => a.order - b.order),
        aiCriteria: (data.criteria ?? [])
          .filter((c: any) => !c.archived)
          .map((c: any) => ({
            id: c.id,
            criterion: c.criterion,
            description: c.description,
            category: c.category,
            order: c.order,
          }))
          .sort((a: CriteriaItem, b: CriteriaItem) => a.order - b.order),
      };
    },
    toPayload: (values: FormValues) => {
      const categoryCounters: Record<string, number> = {};

      const criteriaPayload = values.aiCriteria.map((c) => {
        const idx = categoryCounters[c.category] ?? 0;

        categoryCounters[c.category] = idx + 1;

        return {
          ...(c.id && !c.id.startsWith('_temp_') ? { id: c.id } : {}),
          criterion: c.criterion,
          description: c.description,
          category: c.category,
          order: idx,
        };
      });

      const categoriesPayload = values.categories.map((c) => ({
        name: c.name,
        color: c.color,
        order: c.order,
      }));

      if (isEditing) {
        return {
          criteria: criteriaPayload,
          categories: categoriesPayload,
        };
      }

      return {
        type: 'admission',
        scopeable_id: values.group?.id ?? '',
        scopeable_type: 'Group',
        criteria: criteriaPayload,
        categories: categoriesPayload,
      };
    },
    onCreateSuccess: { toast: 'Admissions criteria successfully created.', navigate: '/admin/admissions/criteria' },
    onUpdateSuccess: {
      toast: isCopyMode ? 'Admissions criteria successfully created.' : 'Admissions criteria successfully updated.',
      navigate: '/admin/admissions/criteria',
    },
    onError: { toast: 'There was an error saving the admissions criteria. Please try again.' },
  });

  // Reset group when client changes (but not on initial data load)
  const prevClientIdRef = useRef(form.values.client?.id);
  const isInitialClientLoad = useRef(true);

  useEffect(() => {
    if (prevClientIdRef.current === form.values.client?.id) return;
    prevClientIdRef.current = form.values.client?.id;

    // Skip resetting group on the initial data load so we don't
    // trigger a validation error before the user has interacted.
    if (isInitialClientLoad.current) {
      isInitialClientLoad.current = false;
      return;
    }

    form.setValue('group', null);
  }, [form.values.client?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const clientOpts = useInfiniteOptions({
    queryKey: ['adminClients', 'aiCriteriaSets'],
    fetchFn: adminIndexClients,
    params: { sortBy: 'name asc' },
  });

  const groupOpts = useInfiniteOptions({
    queryKey: ['adminGroups', 'aiCriteriaSets', form.values.client?.id],
    fetchFn: (params, signal) => adminIndexGroups(params, signal, ['groupType']),
    params: {
      clientId: form.values.client?.id,
      isProvider: true,
      sortBy: 'name asc',
    },
  });

  // Track categories/criteria in a local state pair so we can pass setters to CategoriesList
  // and still keep them in sync with the form hook.
  const [localCategories, setLocalCategories] = useState<CriteriaCategory[]>(form.values.categories);
  const [localCriteria, setLocalCriteria] = useState<CriteriaItem[]>(form.values.aiCriteria);

  // Sync from form -> local when form reinitializes (e.g. data fetch completes)
  const prevFormCategoriesRef = useRef(form.values.categories);
  const prevFormCriteriaRef = useRef(form.values.aiCriteria);

  useEffect(() => {
    if (prevFormCategoriesRef.current !== form.values.categories) {
      prevFormCategoriesRef.current = form.values.categories;
      setLocalCategories(form.values.categories);
    }
    if (prevFormCriteriaRef.current !== form.values.aiCriteria) {
      prevFormCriteriaRef.current = form.values.aiCriteria;
      setLocalCriteria(form.values.aiCriteria);
    }
  }, [form.values.categories, form.values.aiCriteria]);

  // Sync from local -> form when local state changes
  const handleCategoriesChange = (cats: CriteriaCategory[]) => {
    setLocalCategories(cats);
    form.setValue('categories', cats);
  };

  const handleCriteriaChange = (crit: CriteriaItem[]) => {
    setLocalCriteria(crit);
    form.setValue('aiCriteria', crit);
  };

  if (id && form.loading) return <CenteredSpinner />;

  return (
    <AdminFormPage>
      <>
        <FormHeader title={isEditing ? 'Edit Admissions Criteria' : 'Add Admissions Criteria'} />

        <InputGroup title='Client' error={getFieldError(form.errors, 'client')}>
          <Select
            {...clientOpts}
            value={form.values.client}
            onChange={(v) => form.setValue('client', v)}
            getOptionValue={getId}
            getOptionLabel={getName}
            placeholder='Client'
            isDisabled={isEditing || readOnly}
            dataTestId='criteria-form-client'
          />
        </InputGroup>

        <InputGroup title='Group' error={getFieldError(form.errors, 'group')}>
          <Select<ScopeableGroup>
            {...groupOpts}
            value={form.values.group}
            onChange={(v) => form.setValue('group', v)}
            getOptionValue={getId}
            getOptionLabel={(o) => (o?.groupType?.displayName ? `${o.name} (${o.groupType.displayName})` : o?.name)}
            placeholder='Group'
            isDisabled={isEditing || readOnly || !form.values.client}
            dataTestId='criteria-form-group'
          />
        </InputGroup>

        <CategoriesList
          categories={localCategories}
          criteria={localCriteria}
          onCategoriesChange={handleCategoriesChange}
          onCriteriaChange={handleCriteriaChange}
        />

        <StyledButtonGroup>
          <Button variant='ghost' onClick={form.goBack}>
            Cancel
          </Button>
          <Button onClick={form.submit} disabled={readOnly || !form.valid || form.submitting} loading={form.submitting}>
            {isEditing ? 'Update Admissions Criteria' : 'Create Admissions Criteria'}
          </Button>
        </StyledButtonGroup>
      </>
    </AdminFormPage>
  );
}

const StyledButtonGroup = styled(ButtonGroup)`
  margin-top: 40px;
  justify-content: flex-end;
`;

const CenteredSpinner = styled(CircularProgress)`
  margin: 64px auto;
  display: block;
`;
